import React, { createContext, useContext, useRef, useState, ReactNode } from "react";
import { SearchModal } from "../components/search/SearchModal";
import SearchResultsModal from "../features/immunizations/SearchResultsModal";
import AltContactsFormModal from "../features/immunizations/AltContactsModal";
import AvatarListModal from "../features/immunizations/AvatarListModal";
import SmartHealthCardModal from "../features/smart-health-card/SmartHealthCardModal";
import ReportListModal from "../features/immunizations/ReportListModal";
import SmartHealthLinkModal from "../features/smart-health-links/SmartHealthLinkModal";
import { ImmunizationRecord } from "../models/Interfaces";

interface ModalHandle {
  // Search modal methods
  openSearchModal: () => void;
  closeSearchModal: () => void;
  openResultsModal: () => void;
  closeResultsModal: () => void;
  openAltContactsModal: () => void;
  closeAltContactsModal: () => void;

  // Avatar modal methods
  openAvatarListModal: (recordId: string) => void;
  closeAvatarListModal: () => void;

  // Smart Health Card modal methods
  openSmartHealthCardModal: (recordId: string) => void;
  closeSmartHealthCardModal: () => void;

  // Report List modal methods
  openReportListModal: (record: ImmunizationRecord) => void;
  closeReportListModal: () => void;

  // Smart Health Link modal methods
  openSmartHealthLinkModal: (recordId: string) => void;
  closeSmartHealthLinkModal: () => void;
}

const ModalContext = createContext<ModalHandle | null>(null);

export function useModal(): ModalHandle {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error("useModal must be used within ModalProvider");
  }
  return context;
}

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const searchModalRef = useRef<HTMLDialogElement>(null);
  const resultsModalRef = useRef<HTMLDialogElement>(null);
  const altContactsModalRef = useRef<HTMLDialogElement>(null);
  const avatarListModalRef = useRef<HTMLDialogElement>(null);
  const smartHealthCardModalRef = useRef<HTMLDialogElement>(null);
  const reportListModalRef = useRef<HTMLDialogElement>(null);
  const smartHealthLinkModalRef = useRef<HTMLDialogElement>(null);

  const [selectedRecordId, setSelectedRecordId] = useState<string | null>(null);
  const [smartHealthCardRecordId, setSmartHealthCardRecordId] = useState<string | null>(null);
  const [reportListRecord, setReportListRecord] = useState<ImmunizationRecord | null>(null);
  const [smartHealthLinkRecordId, setSmartHealthLinkRecordId] = useState<string | null>(null);

  const value: ModalHandle = {
    openSearchModal: () => searchModalRef.current?.showModal(),
    closeSearchModal: () => searchModalRef.current?.close(),
    openResultsModal: () => resultsModalRef.current?.showModal(),
    closeResultsModal: () => resultsModalRef.current?.close(),
    openAltContactsModal: () => altContactsModalRef.current?.showModal(),
    closeAltContactsModal: () => altContactsModalRef.current?.close(),
    openAvatarListModal: (recordId: string) => {
      setSelectedRecordId(recordId);
      avatarListModalRef.current?.showModal();
    },
    closeAvatarListModal: () => {
      avatarListModalRef.current?.close();
      setSelectedRecordId(null);
    },
    openSmartHealthCardModal: (recordId: string) => {
      setSmartHealthCardRecordId(recordId);
      smartHealthCardModalRef.current?.showModal();
    },
    closeSmartHealthCardModal: () => {
      smartHealthCardModalRef.current?.close();
      setSmartHealthCardRecordId(null);
    },
    openReportListModal: (record: ImmunizationRecord) => {
      setReportListRecord(record);
      reportListModalRef.current?.showModal();
    },
    closeReportListModal: () => {
      reportListModalRef.current?.close();
      setReportListRecord(null);
    },
    openSmartHealthLinkModal: (recordId: string) => {
      setSmartHealthLinkRecordId(recordId);
      smartHealthLinkModalRef.current?.showModal();
    },
    closeSmartHealthLinkModal: () => {
      smartHealthLinkModalRef.current?.close();
      setSmartHealthLinkRecordId(null);
    },
  };

  const handleRunSearch = () => {
    searchModalRef.current?.close();
    resultsModalRef.current?.showModal();
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <SearchModal
        ref={searchModalRef}
        onRunSearch={handleRunSearch}
        onDelete={() => searchModalRef.current?.close()}
      />
      <SearchResultsModal
        ref={resultsModalRef}
        onContinue={() => resultsModalRef.current?.close()}
        onDelete={() => resultsModalRef.current?.close()}
      />
      <AltContactsFormModal
        ref={altContactsModalRef}
        onDelete={() => altContactsModalRef.current?.close()}
        onIncorrectContacts={() => {
          altContactsModalRef.current?.close();
          resultsModalRef.current?.showModal();
        }}
        onVerify={() => altContactsModalRef.current?.close()}
      />
      <AvatarListModal ref={avatarListModalRef} recordId={selectedRecordId} />
      <SmartHealthCardModal ref={smartHealthCardModalRef} recordId={smartHealthCardRecordId} />
      <ReportListModal ref={reportListModalRef} record={reportListRecord} />
      <SmartHealthLinkModal ref={smartHealthLinkModalRef} recordId={smartHealthLinkRecordId} />
    </ModalContext.Provider>
  );
}
